// ──────────────────────────────────────────────────────────────────────────
// purchases.js — thin wrapper around the RevenueCat Capacitor SDK.
//
// Why a wrapper instead of importing Purchases directly at every call site:
//   1. The SDK only loads on native (Capacitor.isNativePlatform()). On
//      web/Vite dev we no-op every method so `npm run dev` doesn't blow up
//      with "RevenueCat not initialized."
//   2. The SDK's API is verbose. Most callers just want "buy this package",
//      "restore", "what's the offering?" — this file provides those.
//   3. Centralizes the App User ID logic. RC needs to be (re)configured on
//      every sign-in / sign-out so purchases attach to the right Supabase
//      user, not the previous one or an anonymous RC ID.
//
// After any successful purchase or restore we ALSO refresh entitlements
// from Supabase. The webhook is the source of truth, but it lands a few
// seconds later — refreshing immediately gives the UI a snappy update,
// and the webhook reconciles any drift.
// ──────────────────────────────────────────────────────────────────────────

// Note: this file targets iOS only. On Electron desktop / plain web we go
// through Stripe Checkout (see ./stripe.js), and every function here returns
// a no-op result so the paywall code can call them unconditionally.
import { isIOS } from './platform.js';
import { refreshEntitlements, broadcastRefresh } from './subscription.js';

// Lazy-load the SDK so a missing pod / web build doesn't crash the app.
let Purchases = null;
let LOG_LEVEL = null;

async function loadSdk() {
  if (Purchases) return Purchases;
  if (!isIOS()) return null;
  try {
    // Variable-indirect the module specifier so Vite's dev-mode import
    // analyzer doesn't try to statically resolve `@revenuecat/purchases-capacitor`
    // (which isn't installed in the desktop project). `/* @vite-ignore */`
    // is supposed to be enough on its own but is ignored for string-literal
    // specifiers; the variable form is the documented escape hatch.
    const rcMod = '@revenuecat/purchases-capacitor';
    const mod = await import(/* @vite-ignore */ rcMod);
    Purchases = mod.Purchases;
    LOG_LEVEL = mod.LOG_LEVEL;
    return Purchases;
  } catch (err) {
    console.error('[purchases] SDK import failed', err);
    return null;
  }
}

let configured = false;
let configuredFor = null; // userId currently configured

// Public API key from the RevenueCat dashboard (NOT the secret key).
// Safe to ship in the bundle — it only authenticates SDK reads.
const RC_API_KEY = import.meta.env.VITE_REVENUECAT_API_KEY || '';

// Configure the SDK with the current Supabase user_id as the App User ID.
// Idempotent — calling it again with the same userId is a no-op. Calling
// it with a new userId reconfigures so future purchases attach correctly.
export async function configurePurchases(userId) {
  const P = await loadSdk();
  if (!P) return false;
  if (!RC_API_KEY) {
    console.warn('[purchases] missing VITE_REVENUECAT_API_KEY — IAP disabled');
    return false;
  }
  if (configured && configuredFor === userId) return true;

  try {
    if (LOG_LEVEL) {
      await P.setLogLevel({ level: import.meta.env.PROD ? LOG_LEVEL.WARN : LOG_LEVEL.INFO });
    }
    if (configured) {
      // SDK is already running for a different user — log them out and
      // reconfigure. logIn alone may be enough but logOut is safer when
      // switching between two real Supabase accounts on the same device.
      try { await P.logOut(); } catch {}
      configured = false;
    }
    await P.configure({
      apiKey: RC_API_KEY,
      appUserID: userId || undefined,  // anonymous if no userId yet
    });
    configured = true;
    configuredFor = userId || null;
    return true;
  } catch (err) {
    console.error('[purchases] configure failed', err);
    return false;
  }
}

export async function logoutPurchases() {
  const P = await loadSdk();
  if (!P || !configured) return;
  try { await P.logOut(); } catch {}
  configured = false;
  configuredFor = null;
}

// Fetch the "default" offering — three packages: monthly, annual, lifetime.
// Returns null on web or any failure so the paywall can fall back to
// hard-coded prices for layout.
export async function getCurrentOffering() {
  const P = await loadSdk();
  if (!P) return null;
  try {
    const { current } = await P.getOfferings();
    return current || null;
  } catch (err) {
    console.error('[purchases] getOfferings failed', err);
    return null;
  }
}

// Purchase a package. The package object comes from getCurrentOffering().
// Returns:
//   { ok: true,  customerInfo }  on success
//   { ok: false, userCancelled } if the user dismissed the StoreKit sheet
//   { ok: false, error }         on any other failure
export async function purchasePackage(pkg) {
  const P = await loadSdk();
  if (!P) return { ok: false, error: new Error('Purchases unavailable on this platform.') };
  try {
    const result = await P.purchasePackage({ aPackage: pkg });
    // Optimistically refresh — the webhook will arrive shortly and flip
    // the canonical tier in Supabase, but this gives the UI an instant
    // signal that the purchase landed.
    await refreshEntitlements();
    broadcastRefresh();
    return { ok: true, customerInfo: result.customerInfo };
  } catch (err) {
    if (err?.userCancelled) return { ok: false, userCancelled: true };
    console.error('[purchases] purchase failed', err);
    return { ok: false, error: err };
  }
}

// Restore prior purchases (Apple's "Restore Purchases" requirement). RC
// reconciles with the App Store and emits webhook events for any active
// entitlement that wasn't already on this user's profile. After we hear
// back, we refresh entitlements to pull whatever the webhook just wrote.
export async function restorePurchases() {
  const P = await loadSdk();
  if (!P) return { ok: false, error: new Error('Purchases unavailable on this platform.') };
  try {
    const { customerInfo } = await P.restorePurchases();
    // Give the webhook a moment to settle, then pull canonical state.
    setTimeout(() => { refreshEntitlements(); broadcastRefresh(); }, 1500);
    await refreshEntitlements();
    return { ok: true, customerInfo };
  } catch (err) {
    console.error('[purchases] restore failed', err);
    return { ok: false, error: err };
  }
}

// Whether IAP is available at all in this build. The paywall reads this
// to decide between showing "Subscribe" buttons or a "Subscriptions are
// available in the iOS app" placeholder for web users.
export function isPurchasesAvailable() {
  return isIOS() && !!RC_API_KEY;
}
